import { useState, useEffect, useCallback } from "react";
import {
  Trash2,
  Users,
  Star,
  Plus,
  ArrowUp,
  AlertCircle,
  Shield,
  Calendar,
  Edit3,
  User,
} from "lucide-react";

import { useTheme } from "../../contexts/ThemeContext";
import { createCharacterCreationStyles } from "../../styles/masterStyles";
import { characterService } from "../../services/characterService";

const CharacterList = ({
  user,
  customUsername,
  selectedCharacterId,
  onSelectedCharacterReset,
  onEditCharacter,
  onDeleteCharacter,
  onCreateNew,
  supabase,
  adminMode = false,
  isUserAdmin = false,
  allCharacters = undefined,
  viewMode = "my",
  refreshTrigger = 0,
}) => {
  const { theme } = useTheme();
  const styles = createCharacterCreationStyles(theme);

  const [savedCharacters, setSavedCharacters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sortBy, setSortBy] = useState("level");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");

  const discordUserId = user?.user_metadata?.provider_id;

  const loadCharacters = useCallback(async () => {
    if (!discordUserId && !adminMode) return;

    setIsLoading(true);
    setError(null);

    try {
      let charactersData;

      if (adminMode && isUserAdmin && allCharacters) {
        charactersData = allCharacters;
      } else if (adminMode && isUserAdmin) {
        charactersData = await characterService.getAllCharacters();
      } else {
        charactersData = await characterService.getCharacters(discordUserId);
      }

      const transformedCharacters = charactersData.map((char) => ({
        abilityScores: char.ability_scores,
        asiChoices: char.asi_choices || {},
        background: char.background,
        backgroundSkills: char.background_skills || [],
        heritageChoices: char.heritage_choices || {},
        innateHeritageSkills: char.innate_heritage_skills || [],
        castingStyle: char.casting_style,
        createdAt: char.created_at,
        currentHitDice: char.current_hit_dice || char.level,
        currentHitPoints: char.current_hit_points ?? char.hit_points,
        discordUserId: char.discord_user_id,
        featChoices: char.feat_choices || {},
        hitPoints: char.hit_points,
        id: char.id,
        imageUrl: char.image_url,
        initiativeAbility: char.initiative_ability,
        innateHeritage: char.innate_heritage,
        level: char.level,
        level1ChoiceType: char.level1_choice_type,
        magicModifiers: char.magic_modifiers || {},
        maxHitDice: char.max_hit_dice || char.level,
        name: char.name,
        skillExpertise: char.skill_expertise || [],
        skillProficiencies: char.skill_proficiencies || [],
        standardFeats: char.standard_feats || [],
        subclass: char.subclass,
        subclassChoices: char.subclass_choices || {},
        tempHitPoints: char.temp_hit_points || 0,
        updatedAt: char.updated_at,
        wandCore: char.wand_core,
        wandFlexibility: char.wand_flexibility,
        wandLength: char.wand_length,
        wandType: char.wand_type,
        wandWood: char.wand_wood,
      }));

      let filteredCharacters = transformedCharacters;
      if (viewMode === "my" && !adminMode) {
        filteredCharacters = transformedCharacters.filter(
          (char) => char.discordUserId === discordUserId
        );
      }

      setSavedCharacters(filteredCharacters);
    } catch (err) {
      console.error("Error loading characters:", err);
      setError("Failed to load characters. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [discordUserId, adminMode, isUserAdmin, allCharacters, viewMode]);

  useEffect(() => {
    loadCharacters();
  }, [loadCharacters, refreshTrigger]);

  const sortedAndFilteredCharacters = savedCharacters
    .filter((char) => {
      const matchesSearch = searchTerm
        ? char.name.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      return matchesSearch;
    })
    .sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "level":
          comparison = a.level - b.level;
          break;
        case "created":
        default:
          comparison = new Date(a.createdAt) - new Date(b.createdAt);
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

  const handleDeleteCharacter = async (character) => {
    if (onDeleteCharacter) {
      await onDeleteCharacter(character);
    }
  };

  const renderCharacterCard = (character) => {
    const isSelectedCharacter = selectedCharacterId === character.id;

    return (
      <div
        key={character.id}
        style={{
          ...styles.panel,
          padding: "20px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          gap: "20px",
          cursor: "pointer",
          border: isSelectedCharacter
            ? `2px solid ${theme.primary}`
            : `1px solid ${theme.border}`,
          backgroundColor: isSelectedCharacter
            ? theme.surface
            : theme.background,
          transition: "all 0.2s ease",
          boxShadow: isSelectedCharacter
            ? `0 4px 12px ${theme.primary}20`
            : "0 2px 4px rgba(0,0,0,0.1)",
        }}
        onClick={() => onEditCharacter && onEditCharacter(character)}
      >
        <div
          style={{
            width: "80px",
            height: "80px",
            borderRadius: "12px",
            overflow: "hidden",
            backgroundColor: theme.surface,
            border: `2px solid ${theme.border}`,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "120px",
          }}
        >
          {character.imageUrl ? (
            <img
              src={character.imageUrl}
              alt={character.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = "flex";
                }
              }}
            />
          ) : null}
          <div
            style={{
              display: character.imageUrl ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              backgroundColor: theme.surface,
            }}
          >
            <User size={32} style={{ color: theme.textSecondary }} />
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <h3
            style={{
              ...styles.title,
              fontSize: "20px",
              marginBottom: "6px",
              color: theme.text,
            }}
          >
            {character.name}
          </h3>
          <p
            style={{
              ...styles.subtitle,
              fontSize: "14px",
              marginBottom: "4px",
            }}
          >
            Level {character.level} {character.castingStyle}
          </p>
          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {character.subclass && (
              <span
                style={{
                  padding: "2px 8px",
                  borderRadius: "4px",
                  backgroundColor: `${theme.secondary}20`,
                  color: theme.secondary,
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                {character.subclass}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEditCharacter && onEditCharacter(character);
            }}
            style={{
              ...styles.button,
              backgroundColor: theme.primary,
              color: "white",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              borderRadius: "6px",
              fontSize: "13px",
            }}
            title="Edit"
          >
            <Edit3 size={14} />
            <span
              style={{ display: window.innerWidth > 768 ? "inline" : "none" }}
            >
              Edit
            </span>
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteCharacter(character);
            }}
            style={{
              ...styles.button,
              backgroundColor: theme.error,
              color: "white",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              borderRadius: "6px",
              fontSize: "13px",
            }}
            title="Delete"
          >
            <Trash2 size={14} />
            <span
              style={{ display: window.innerWidth > 768 ? "inline" : "none" }}
            >
              Delete
            </span>
          </button>
        </div>
      </div>
    );
  };

  if (isLoading && savedCharacters.length === 0) {
    return (
      <div style={{ ...styles.panel, padding: "40px", textAlign: "center" }}>
        <p style={styles.subtitle}>Loading characters...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...styles.panel, padding: "40px", textAlign: "center" }}>
        <AlertCircle
          size={24}
          style={{ color: theme.error, marginBottom: "8px" }}
        />
        <p style={{ color: theme.error }}>{error}</p>
      </div>
    );
  }

  if (savedCharacters.length === 0) {
    return (
      <div style={{ ...styles.panel, padding: "60px", textAlign: "center" }}>
        <Users
          size={64}
          style={{ color: theme.textSecondary, marginBottom: "20px" }}
        />
        <h3 style={{ ...styles.title, fontSize: "24px", marginBottom: "8px" }}>
          No Characters Yet
        </h3>
        <p style={{ ...styles.subtitle, marginBottom: "24px" }}>
          Create your first character to get started!
        </p>
        {onCreateNew && (
          <button
            onClick={onCreateNew}
            style={{
              ...styles.button,
              backgroundColor: theme.primary,
              color: "white",
              padding: "14px 28px",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "16px",
              borderRadius: "8px",
            }}
          >
            <Plus size={18} />
            Create Character
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          ...styles.panel,
          padding: "20px",
          marginBottom: "24px",
          position: "relative",
        }}
      >
        {isLoading && savedCharacters.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              padding: "4px 8px",
              backgroundColor: theme.primary,
              color: "white",
              borderRadius: "4px",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            Refreshing...
          </div>
        )}
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="text"
            placeholder="Search characters by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              ...styles.input,
              flex: "1 1 250px",
              margin: 0,
              padding: "10px 14px",
              fontSize: "14px",
            }}
          />

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              ...styles.select,
              padding: "10px 14px",
              fontSize: "14px",
              minWidth: "140px",
            }}
          >
            <option value="level">Sort by Level</option>
            <option value="name">Sort by Name</option>
            <option value="created">Sort by Date</option>
          </select>

          <button
            onClick={() =>
              setSortDirection(sortDirection === "asc" ? "desc" : "asc")
            }
            style={{
              ...styles.button,
              backgroundColor: theme.surface,
              color: theme.text,
              padding: "10px 14px",
              fontSize: "18px",
              borderRadius: "6px",
              minWidth: "44px",
            }}
            title={
              sortDirection === "asc" ? "Sort Ascending" : "Sort Descending"
            }
          >
            {sortDirection === "asc" ? "↑" : "↓"}
          </button>
        </div>
      </div>

      <div
        style={{
          opacity: isLoading && savedCharacters.length > 0 ? 0.7 : 1,
          transition: "opacity 0.2s ease",
        }}
      >
        {sortedAndFilteredCharacters.map(renderCharacterCard)}
      </div>

      {sortedAndFilteredCharacters.length > 0 && (
        <div
          style={{
            ...styles.panel,
            padding: "16px",
            marginTop: "24px",
            textAlign: "center",
          }}
        >
          <p style={styles.subtitle}>
            Showing {sortedAndFilteredCharacters.length} of{" "}
            {savedCharacters.length} characters
            {searchTerm && ` matching "${searchTerm}"`}
          </p>
        </div>
      )}
    </div>
  );
};

export default CharacterList;
